import React, { useState } from "react";
import {
  Alert,
  AlertTitle,
  AppBar,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Slide,
  Tab,
  Tabs,
  Toolbar,
  Typography,
  Fab,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { Close, DeveloperMode } from "@mui/icons-material";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object } from "yup";
import { socialDataAndChargesSchema } from "./schema/socialDataAndChargesSchema";
import { admissionSchema } from "./schema/admissionSchema";
import { payrollSchema } from "./schema/payrollSchema";
import { bankCommunicationSchema } from "./schema/bankCommunicationSchema";
import { businessRuleSchema } from "./schema/businessRuleSchema";
import { benefitsSchema } from "./schema/benefitsSchema";
import SocialDataAndCharges from "./components/SocialDataAndCharges";
import Admission from "./components/Admission";
import Payroll from "./components/Payroll";
import BankCommunication from "./components/BankCommunication";
import BusinessRule from "./components/BusinessRule";
import Benefits from "./components/Benefits";
import { demoValues } from "./demoValues";
import { useAuth } from "../../../../contexts/AuthContext";
import { useCreateOnboarding } from "./hooks/useOnboarding";
import { useGetOnboarding } from "./hooks/useGetOnboarding";
import { toast } from "react-toastify";

const APP_STAGE = process.env.REACT_APP_STAGE;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const tabs = [
  "Dados sociais & encargos",
  "Admissão",
  "Folha de pagamento",
  "Comunicação bancária",
  "Regra de negócio",
  "Benefício",
];

const allSchemas = [
  socialDataAndChargesSchema,
  admissionSchema,
  payrollSchema,
  bankCommunicationSchema,
  businessRuleSchema,
  benefitsSchema,
];

const combinedSchema = object().shape(
  allSchemas.reduce((acc, schema) => ({ ...acc, ...schema.fields }), {}),
);

const defaultValues = {
  socialDataAndChargesIsValidated: false,
  companyCnpj: "",
  companyName: "",
  taxRegime: "",
  mainCnae: "",
  companyHasAgreements: "",
  emitBoletoSeparately: "",
  isEmpresaCidada: "",
  hasCollectiveVacation: "",
  collectiveVacationPeriod: [],
  collectiveVacationDays: "",
  collectiveVacationByCostCenter: "",
  collectiveVacationOtherRule: "",
  admissionIsValidated: false,
  trialContractTerm: "",
  trialContractTermOther: "",
  hasSpecificRegistrationRules: "",
  specificRegistrationRulesDescription: "",
  useSamePayrollRegistrationForTimeTracking: "",
  workTimeControl: "",
  timeTrackingClosePeriod: "",
  timeTrackingClosePeriodOther: "",
  hasBankOfHours: "",
  bankOfHoursFrequency: "",
  bankOfHoursCompetencies: [],
  payrollIsValidated: false,
  calendarDefaultValues: [],
  ...[1, 2, 3, 4, 5, 6, 7, 8, 9].reduce((acc, id) => {
    acc[`notApplicable-${id}`] = false;
    acc[`paymentDay-${id}`] = "";
    return acc;
  }, {}),
  anticipatePaymentsOnWeekendsHolidays: "",
  anticipatePaymentsDescription: "",
  hasMonthlyAdvance: "",
  advancePercentage: "",
  advanceCalcBase: "",
  advanceCalcBaseForCommissioned: "",
  advanceCalcBaseForCommissionedOther: "",
  minDaysWorkedForAdvance: "",
  advancePaymentCalculationType: "",
  newHiresReceiveAdvance: "",
  advanceAdhesionNextMonth: "",
  maternityLeaveReceiveAdvance: "",
  proLaboreReceiveAdvance: "",
  internReceiveAdvance: "",
  apprenticeReceiveAdvance: "",
  irrfAdvanceRetentionRule: "",
  irrfAdvanceRetentionRuleOther: "",
  advanceAnalyticalReportOrder: "",
  advanceNetReport: "",
  advanceNetReportExcel: "",
  thirteenthFirstInstallmentWriteOff: "",
  vacationWriteOffMethod: "",
  resignationProvisionWhenTerminated: "",
  provisionAnalyticalReportOrder: "",
  provisionSyntheticReportOrder: "",
  provisionAccountingSyntheticReportOrder: "",
  thirteenthAdvanceOnBirthday: "",
  thirteenthFirstInstallmentWithAverages: "",
  thirteenthFirstInstallmentWithAveragesOther: "",
  internProLaboreThirteenth: "",
  internProLaboreThirteenthRubricsNovDec: "",
  internProLaboreThirteenthRubricsDecReceive: "",
  internProLaboreThirteenthRubricsDecOnly: "",
  internProLaboreThirteenthOtherRule: "",
  thirteenthAnalyticalReportOrder: "",
  thirteenthSyntheticReportOrder: "",
  thirteenthNetReport: "",
  thirteenthNetReportExcel: "",
  hasPlr: "",
  plrAccordingToCctAct: "",
  plrBusinessRule: "",
  plrFrequencyAndCompetencies: "",
  plrSameCalendarYear: [],
  plrAnalyticalReportOrder: "",
  plrSyntheticReportOrder: "",
  plrNetReport: "",
  plrNetReportExcel: "",
  plrAccountingSyntheticReportOrder: "",
  hasConsignedLoan: "",
  consignedLoanDeductionLaw: "",
  consignedLoanDeductionOther: "",
  consignedLoanFullDeductionIfNoBalance: "",
  consignedLoanFullDeductionIfNoBalanceOther: "",
  consignedLoanPeriod: "",
  consignedLoanFormat: "",
  consignedLoanDataReceptionDate: "",
  bankCommunicationIsValidated: false,
  hasBankTransferFile: "",
  bankFileLayoutD1: "",
  bankSequentialNumberRule: "",
  issueUnifiedBankFile: "",
  bankAccounts: [
    {
      cnpj: "",
      bank: "",
      agency: "",
      account: "",
      agreementCode: "",
    },
  ],
  specificPaymentRule: "",
  specificPaymentRuleDescription: "",
  businessRuleIsValidated: false,
  collectiveAgreementType: "",
  applyCollectiveAgreementRules: "",
  collectiveAgreementPartialClauses: "",
  applyCollectiveAgreementRulesOther: "",
  vacationAdvanceDiscounts: "",
  vacationAdvanceDiscountsList: "",
  vacationDiscountConditions: "",
  vacationAverages: "",
  vacationAveragesOther: "",
  vacationBenefits: "",
  vacationBenefitsList: "",
  vacationBenefitsDescription: "",
  terminationDiscounts: [],
  terminationDiscountsOther: "",
  loanDiscountPercentageTermination: "",
  loanDiscountPercentageTerminationOther: "",
  terminationAverages: "",
  terminationAveragesOther: "",
  payrollHasInterns: "",
  internSalaryAdjustmentRule: "",
  internSalaryAdjustmentOther: "",
  internHasDeductions: "",
  internDeductionsDescription: "",
  internHasBenefits: "",
  interBenefitsCalculation: "",
  interPayrollProcessing: "",
  internFixedBenefit: "",
  internFixedBenefitRubric: "",
  internFixedBenefitRule: "",
  internBenefitValue: "",
  internProportionalCalcRule: "",
  internProportionalCalcRuleOther: "",
  payrollHasApprentices: "",
  apprenticeSalaryAdjustmentRule: "",
  apprenticeSalaryAdjustmentOther: "",
  apprenticeHasDeductions: "",
  apprenticeDeductionsDescription: "",
  apprenticeHasBenefits: "",
  apprenticeBenefitsCalculation: "",
  apprenticePayrollProcessing: "",
  apprenticeFixedBenefit: "",
  apprenticeFixedBenefitRubric: "",
  apprenticeFixedBenefitRule: "",
  apprenticeBenefitValue: "",
  apprenticeProportionalCalcRule: "",
  apprenticeProportionalCalcRuleOther: "",
  hasAutonomous: "",
  autonomousBusinessRule: "",
  hasProLabore: "",
  proLaboreSalaryAdjustmentRule: "",
  proLaboreSalaryAdjustmentOther: "",
  proLaboreHasFgtsNoLink: "",
  payrollAnalyticalReportOrder: "",
  payrollSyntheticReportOrder: "",
  payrollNetReport: "",
  payrollNetReportExcel: "",
  payrollAccountingSyntheticReportOrder: "",
  hasDomesticEmployee: "",
  domesticEmployeeSalaryAdjustmentRule: "",
  domesticEmployeeSalaryAdjustmentRuleOther: "",
  domesticEmployeeWorkTimeControl: "",
  domesticEmployeeClosePeriod: "",
  domesticEmployeeClosePeriodOther: "",
  hasSpecificWageItems: "",
  specificWageRows: {},
  hasCustomWageIncidences: "",
  customWageRows: {},
  hasHazardPay: "",
  hasInsalubrityPay: "",
  discountAdditionalOnAbsence: "",
  proportionalRuleForDifferentMonthDays: "",
  otherExclusiveRules: "",
  otherExclusiveRulesDescription: "",
  benefitsIsValidated: false,
  hasLifeInsurance: "",
  lifeInsuranceMovementType: "",
  lifeInsuranceMovementReport: "",
  lifeInsuranceMovementReportDeadline: "",
  lifeInsuranceDiscountValue: "",
  lifeInsuranceDiscountValueFix: "",
  lifeInsuranceDiscountValueSalaryPercentage: "",
  lifeInsuranceDiscountValueOther: "",
  lifeInsuranceCompanyCost: "",
  lifeInsuranceEmployeeCost: "",
  lifeInsuranceProLabore: "",
  lifeInsuranceIntern: "",
  lifeInsuranceApprentice: "",
  lifeInsuranceOnLeave: "",
  lifeInsuranceDiscountOnPayroll: "",
  lifeInsuranceDiscountOnPayrollOther: "",
  lifeInsuranceDiscountInTermination: "",
  lifeInsuranceOtherRules: "",
  lifeInsuranceOtherRulesDescription: "",
  hasHealthPlan: "",
  healthPlanOperators: [
    {
      name: "",
      cnpj: "",
      ansCode: "",
      type: "",
      companyCost: "",
      employeeCost: "",
      dependentCost: "",
      aggregateCost: "",
      hasCoparticipation: "",
      companyCoparticipationCost: "",
      employeeCoparticipationCost: "",
      dependentCoparticipationCost: "",
      aggregateCoparticipationCost: "",
      coparticipationRule: "",
      coparticipationRuleDescription: "",
      beneficiariesOutsideEmployees: "",
      beneficiariesOutsideEmployeesDescription: "",
      reportReceptionDate: "",
      adjustmentDate: "",
    },
  ],
  healthPlanDiscountOnPayroll: "",
  healthPlanDiscountOnPayrollOther: "",
  healthPlanDiscountInTermination: "",
  healthPlanOtherRules: "",
  healthPlanOtherRulesDescription: "",
  hasDentalPlan: "",
  dentalPlanOperators: [
    {
      name: "",
      cnpj: "",
      ansCode: "",
      type: "",
      companyCost: "",
      employeeCost: "",
      dependentCost: "",
      aggregateCost: "",
      beneficiariesOutsideEmployees: "",
      beneficiariesOutsideEmployeesDescription: "",
      reportReceptionDate: "",
      adjustmentDate: "",
    },
  ],
  dentalPlanDiscountOnPayroll: "",
  dentalPlanDiscountOnPayrollOther: "",
  dentalPlanDiscountInTermination: "",
  dentalPlanOtherRules: "",
  dentalPlanOtherRulesDescription: "",
  hasTransportationVoucher: "",
  transportationOperatorName: "",
  transportationCalculationPeriod: "",
  transportationDeductionPeriod: "",
  transportationCalcExecution: "",
  transportationPurchaseDeadline: "",
  transportationDeductAbsences: "",
  transportationDeductMedicalCertificates: "",
  transportationBenefitForAccidentLeave: "",
  transportationAccidentLeaveLimit: "",
  transportationBenefitForAccidentLeaveOther: "",
  transportationBenefitForIllnessLeave: "",
  transportationBenefitForIllnessLeaveLimit: "",
  transportationBenefitForIllnessLeaveOther: "",
  transportationBenefitForMaternityLeave: "",
  transportationBenefitForMaternityLeaveLimit: "",
  transportationBenefitForMaternityLeaveOther: "",
  transportationBenefitDuringVacation: "",
  transportationOtherDeductionFactors: "",
  transportationDeductionRuleDescription: "",
  transportationPayrollDeduction: "",
  transportationPayrollDeductionOther: "",
  transportationTerminationDeduction: "",
  transportationUnusedBenefitDeduction: "",
  hasMobilityVoucher: "",
  mobilityOperatorName: "",
  mobilityCalculationPeriod: "",
  mobilityDeductionPeriod: "",
  mobilityCalcExecution: "",
  mobilityPurchaseDeadline: "",
  mobilityDeductAbsences: "",
  mobilityDeductMedicalCertificates: "",
  mobilityBenefitForAccidentLeave: "",
  mobilityAccidentLeaveLimit: "",
  mobilityBenefitForAccidentLeaveOther: "",
  mobilityBenefitForIllnessLeave: "",
  mobilityBenefitForIllnessLeaveLimit: "",
  mobilityBenefitForIllnessLeaveOther: "",
  mobilityBenefitForMaternityLeave: "",
  mobilityBenefitForMaternityLeaveLimit: "",
  mobilityBenefitForMaternityLeaveOther: "",
  mobilityBenefitDuringVacation: "",
  mobilityOtherDeductionFactors: "",
  mobilityDeductionRuleDescription: "",
  mobilityPayrollDeduction: "",
  mobilityPayrollDeductionOther: "",
  mobilityTerminationDeduction: "",
  mobilityUnusedBenefitDeduction: "",
  hasFoodVoucher: "",
  foodOperatorName: "",
  foodCalculationPeriod: "",
  foodDeductionPeriod: "",
  foodCalcExecution: "",
  foodPurchaseDeadline: "",
  foodDeductAbsences: "",
  foodDeductMedicalCertificates: "",
  foodBenefitForAccidentLeave: "",
  foodAccidentLeaveLimit: "",
  foodBenefitForAccidentLeaveOther: "",
  foodBenefitForIllnessLeave: "",
  foodBenefitForIllnessLeaveLimit: "",
  foodBenefitForIllnessLeaveOther: "",
  foodBenefitForMaternityLeave: "",
  foodBenefitForMaternityLeaveLimit: "",
  foodBenefitForMaternityLeaveOther: "",
  foodBenefitDuringVacation: "",
  foodOtherDeductionFactors: "",
  foodDeductionRuleDescription: "",
  foodPayrollDeduction: "",
  foodPayrollDeductionOther: "",
  foodTerminationDeduction: "",
  foodUnusedBenefitDeduction: "",
  hasMealVoucher: "",
  mealOperatorName: "",
  mealCalculationPeriod: "",
  mealDeductionPeriod: "",
  mealCalcExecution: "",
  mealPurchaseDeadline: "",
  mealDeductAbsences: "",
  mealDeductMedicalCertificates: "",
  mealBenefitForAccidentLeave: "",
  mealAccidentLeaveLimit: "",
  mealBenefitForAccidentLeaveOther: "",
  mealBenefitForIllnessLeave: "",
  mealBenefitForIllnessLeaveLimit: "",
  mealBenefitForIllnessLeaveOther: "",
  mealBenefitForMaternityLeave: "",
  mealBenefitForMaternityLeaveLimit: "",
  mealBenefitForMaternityLeaveOther: "",
  mealBenefitDuringVacation: "",
  mealOtherDeductionFactors: "",
  mealDeductionRuleDescription: "",
  mealPayrollDeduction: "",
  mealPayrollDeductionOther: "",
  mealTerminationDeduction: "",
  mealUnusedBenefitDeduction: "",
  hasBasicBasket: "",
  basicBasketProvider: "",
  basicBasketCalculationPeriod: "",
  basicBasketBusinessRule: "",
  basicBasketPayrollDeduction: "",
  basicBasketPayrollDeductionOther: "",
  basicBasketTerminationDeduction: "",
  basicBasketUnusedBenefitDeduction: "",
  hasCanteen: "",
  canteenCalculationPeriod: "",
  canteenBusinessRule: "",
  canteenPayrollDeduction: "",
  canteenPayrollDeductionOther: "",
  canteenTerminationDeduction: "",
  canteenUnusedBenefitDeduction: "",
  hasOtherBenefits: "",
  otherBenefits: [{ description: "", businessRule: "" }],
  hasPharmacy: "",
  agreements: [
    {
      description: "",
      calculationPeriod: "",
      infoFormat: "",
      reportDeadline: "",
      payrollDeduction: "",
      payrollDeductionOther: "",
      terminationDeduction: "",
      unusedBenefitDeduction: "",
    },
  ],
  hasSocialSecurity: "",
  socialSecurityUser: "",
  socialSecurityPassword: "",
  hasPowerOfAttorney: "",
  eSocialPowerOfAttorneyExpirationDate: "",
  connectivityPowerOfAttorneyExpirationDate: "",
  employerWebPowerOfAttorneyExpirationDate: "",
};

export default function Registration() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);
  const [schemaErrors, setSchemaErrors] = useState([]);

  const companyCode = searchParams.get("company_code") || "";

  const isStaff = user?.groups
    ?.map(group => group.name)
    ?.includes("analista-folha");

  const methods = useForm({
    defaultValues,
    mode: "all",
    resolver: yupResolver(combinedSchema),
  });

  const {
    mutate: createOnboarding,
    isLoading: isCreating,
  } = useCreateOnboarding({
    onSuccess: () => {
      toast.success("Dados de onboarding salvos com sucesso!");
      handleCloseDialog();
    },
    onError: error => {
      toast.error("Erro ao salvar dados de onboarding.");
      console.error("Error saving onboarding data", error);
    },
  });

  const { data: onboardingData, isLoading: isFetching } = useGetOnboarding(
    companyCode,
  );

  React.useEffect(() => {
    if (onboardingData?.formData) {
      let parsedData = {};
      try {
        parsedData =
          typeof onboardingData.formData === "string"
            ? JSON.parse(onboardingData.formData)
            : onboardingData.formData;
      } catch (e) {
        console.error("Failed to parse formData", e);
      }
      methods.reset({ ...defaultValues, ...parsedData });
    }
  }, [onboardingData, methods]);

  const handleCloseDialog = () => {
    searchParams.delete("screen");
    setSearchParams(searchParams);
  };

  const handleDevFill = () => {
    methods.reset(demoValues);
  };

  const handleSave = () => {
    setSchemaErrors([]);
    const formData = methods.getValues();

    createOnboarding({
      companyCode: companyCode,
      formData: formData,
      closedVersion: false,
    });
  };

  const handleSend = async () => {
    const isValid = await methods.trigger();

    if (!isValid) {
      const fieldErrors = methods.formState.errors;
      const messages = Object.values(fieldErrors)
        .map(e => e?.message)
        .filter(Boolean);
      setSchemaErrors(messages);
      toast.error("Corrija os campos obrigatórios antes de enviar");
      return;
    }

    setSchemaErrors([]);
    const formData = methods.getValues();

    createOnboarding({
      companyCode: companyCode,
      formData: formData,
      closedVersion: true,
    });
  };

  return (
    <FormProvider {...methods}>
      <Dialog
        fullScreen
        open
        onClose={handleCloseDialog}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleCloseDialog}
              aria-label="close"
            >
              <Close />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Onboarding
              {onboardingData?.version && (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ ml: 2, opacity: 0.8 }}
                >
                  (Versão: {onboardingData.version})
                </Typography>
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                color="inherit"
                variant="outlined"
                onClick={handleSave}
                disabled={isCreating || isFetching}
              >
                {isCreating ? "Salvando..." : "Salvar"}
              </Button>
              <Button
                color="secondary"
                variant="contained"
                onClick={handleSend}
                disabled={isCreating || isFetching}
              >
                {isCreating ? "Enviando..." : "Enviar"}
              </Button>
            </Box>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            px: { xs: 2, sm: 4 },
            py: 3,
            textAlign: "center",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Alert severity="info" sx={{ mx: "auto", textAlign: "left" }}>
            <AlertTitle>Bem-vindo ao processo de Onboarding</AlertTitle>
            Preencha as informações da empresa navegando pelas abas abaixo. Você pode clicar em <strong>Salvar</strong> a qualquer momento para não perder seu progresso (sem validação obrigatória). Ao finalizar o preenchimento de todos os dados obrigatórios, clique em <strong>Enviar</strong> para concluir o onboarding de forma definitiva.
          </Alert>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            centered
            sx={{ mt: 2 }}
          >
            {tabs.map((label, index) => (
              <Tab key={index} label={label} />
            ))}
          </Tabs>
        </Box>

        <DialogContent sx={{ p: { xs: 1, sm: 2 } }}>
          {schemaErrors.length > 0 && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setSchemaErrors([])}
            >
              <AlertTitle>Corrija os campos antes de salvar</AlertTitle>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {schemaErrors.map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
              </ul>
            </Alert>
          )}
          {
            [
              <SocialDataAndCharges isStaff={isStaff} />,
              <Admission isStaff={isStaff} />,
              <Payroll isStaff={isStaff} />,
              <BankCommunication isStaff={isStaff} />,
              <BusinessRule isStaff={isStaff} />,
              <Benefits isStaff={isStaff} />,
            ][activeTab]
          }
        </DialogContent>
        {APP_STAGE === "development" && (
          <Fab
            color="primary"
            aria-label="add"
            style={{
              position: "fixed",
              bottom: "16px",
              right: "16px",
              backgroundColor: "red",
            }}
            onClick={handleDevFill}
          >
            <DeveloperMode />
          </Fab>
        )}
      </Dialog>
    </FormProvider>
  );
}
